import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useDeleteEvent } from '@/hooks/useDeleteEvent';
import { getTagValue, STELE_SPACE_KIND } from '@/lib/stele';
import { Loader2, Trash2, Download } from 'lucide-react';
import { useSpacePages } from '@/hooks/usePages';
import { exportSpaceAsZip } from '@/lib/exportSpace';

interface SpaceSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spaceEvent: NostrEvent;
}

const EMOJI_OPTIONS = ['📖', '📚', '🔧', '💡', '🚀', '🎯', '📝', '🔬', '🎨', '🏗️', '⚡', '🌟'];

export function SpaceSettingsDialog({ open, onOpenChange, spaceEvent }: SpaceSettingsDialogProps) {
  const currentTitle = getTagValue(spaceEvent.tags, 'title') ?? '';
  const currentDescription = getTagValue(spaceEvent.tags, 'description') ?? '';
  const currentIcon = getTagValue(spaceEvent.tags, 'icon') ?? '📖';
  const dTag = getTagValue(spaceEvent.tags, 'd') ?? '';

  const [title, setTitle] = useState(currentTitle);
  const [description, setDescription] = useState(currentDescription);
  const [icon, setIcon] = useState(currentIcon);

  const { mutateAsync: publishEvent, isPending } = useNostrPublish();
  const { mutateAsync: deleteEvent, isPending: isDeleting } = useDeleteEvent();
  const { data: pageEvents } = useSpacePages(spaceEvent.pubkey, dTag.replace('stele:space:', ''));
  const [isExporting, setIsExporting] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleSave = async () => {
    // Keep page tags and update metadata tags
    const pageTags = spaceEvent.tags.filter(t => t[0] === 'page');
    await publishEvent({
      kind: STELE_SPACE_KIND,
      content: '',
      tags: [
        ['d', dTag],
        ['title', title.trim()],
        ['description', description.trim()],
        ['icon', icon],
        ['alt', `Stele documentation space: ${title.trim()}`],
        ['client', 'stele'],
        ...pageTags,
      ],
    });
    await queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this entire space? All pages will also be deleted.')) return;

    await deleteEvent({
      kind: STELE_SPACE_KIND,
      dTag,
      eventId: spaceEvent.id,
    });

    await queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
    onOpenChange(false);
    navigate('/');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Space Settings</DialogTitle>
          <DialogDescription>Update your space metadata or delete the space.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map(e => (
                <button
                  key={e}
                  type="button"
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg transition-all ${
                    icon === e
                      ? 'bg-primary/10 ring-2 ring-primary'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  onClick={() => setIcon(e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="settings-title">Title</Label>
            <Input
              id="settings-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="settings-description">Description</Label>
            <Textarea
              id="settings-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending || !title.trim()}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>

          <Separator />

          <div className="pt-2">
            <h4 className="text-sm font-semibold mb-2">Export</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!pageEvents) return;
                setIsExporting(true);
                try {
                  await exportSpaceAsZip({ spaceEvent, pageEvents });
                } finally {
                  setIsExporting(false);
                }
              }}
              disabled={isExporting || !pageEvents}
              className="gap-2"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export as Markdown
            </Button>
            <p className="text-xs text-muted-foreground mt-1">Download a .zip of all pages as .md files.</p>
          </div>

          <Separator />

          <div className="pt-2">
            <h4 className="text-sm font-semibold text-destructive mb-2">Danger Zone</h4>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              className="gap-2"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete Space
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
